import { type FC, useCallback, useState } from "react";

import type { DashboardPendingInvite } from "../../../dashboard-client";

import { Alert } from "../../common/Alert";
import { Button } from "../../common/Button";

interface PendingInvitesStepProps {
  invites: DashboardPendingInvite[];
  onAccept: (token: string) => Promise<void>;
  onSkip: () => void;
  submitting: boolean;
  error: string | null;
}

export const PendingInvitesStep: FC<PendingInvitesStepProps> = ({
  invites,
  onAccept,
  onSkip,
  submitting,
  error,
}) => {
  const [acceptingToken, setAcceptingToken] = useState<string | null>(null);

  const handleAccept = useCallback(
    (token: string) => {
      setAcceptingToken(token);
      void onAccept(token);
    },
    [onAccept],
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white">
          You have pending invites
        </h2>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          You've been invited to join the following organizations. Accept an
          invite to get started, or skip to create your own.
        </p>
      </div>

      <div className="space-y-2">
        {invites.map((invite) => (
          <div
            key={invite.id}
            className="flex items-center justify-between border border-slate-700 bg-arti-background p-3"
          >
            <div>
              <span className="font-mono text-sm font-bold text-white">
                {invite.organization_name}
              </span>
              <span className="ml-2 border border-slate-700 bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {invite.role}
              </span>
              {invite.inviter_name && (
                <p className="mt-0.5 font-mono text-[10px] text-slate-500">
                  Invited by {invite.inviter_name}
                </p>
              )}
            </div>
            <Button
              size="sm"
              onClick={() => handleAccept(invite.token)}
              loading={submitting && acceptingToken === invite.token}
              loadingText="Joining..."
              disabled={submitting}
            >
              Accept
            </Button>
          </div>
        ))}
      </div>

      {error && (
        <Alert variant="error" animate>
          {error}
        </Alert>
      )}

      <Button onClick={onSkip} variant="ghost" disabled={submitting} fullWidth>
        Skip — create a new organization instead
      </Button>
    </div>
  );
};
